/**
 * HelpPageViewModel - Help Page Orchestration
 *
 * Manages help page functionality including navigation tiles,
 * form display, form submissions, and restaurant contact
 *
 * Cross-platform implementation with dependency injection
 */

import { useCallback, useState, useEffect, useRef } from 'react'
import type { Order, HelpPageTile } from '@restaurant/core'
import { safeGetLogger } from '@restaurant/logging'

import type {
  IPlatformServices,
  IHelpPageService,
  HelpFormData,
  RestaurantContactInfo
} from '../contracts'

// Active section type
export type HelpSection = 'navigation' | 'order-issue' | 'app-issue' | 'feedback'

// Navigation item interface (matching HelpPageNavigation component)
interface HelpNavItem {
  id: string
  pageTitle: string
  iconName: HelpPageTile['iconName']
  onNavigate?: () => void
  disabled?: boolean
  testID?: string
}

// HelpPageNavigation props interface
interface HelpPageNavigationProps {
  navItems?: HelpNavItem[]
  testID?: string
}

// Form props interface (matching SectionOrderIssueForm, SectionAppIssueForm, SectionFeedbackForm)
interface FormProps {
  specialRequests?: string
  onSpecialRequestsChange?: (text: string) => void
  onSubmit: () => void
  loading?: boolean
  disabled?: boolean
  testID?: string
  accessibilityLabel?: string
}

// PageContentHelp props interface
interface PageContentHelpPropsResult {
  tiles: Array<{
    id: string
    pageTitle: string
    iconName: HelpPageTile['iconName']
    onClick?: () => void
    disabled?: boolean
    testID?: string
    accessibilityLabel?: string
  }>
  appIssueSheetOpen: boolean
  onAppIssueSheetClose: () => void
  feedbackSheetOpen: boolean
  onFeedbackSheetClose: () => void
  appIssueFormProps: FormProps
  feedbackFormProps: FormProps
  // Order issue sheets
  orderHistorySheetOpen: boolean
  onOrderHistorySheetClose: () => void
  orderHistorySheetProps: OrderHistorySheetProps
  orderIssueSheetOpen: boolean
  onOrderIssueSheetClose: () => void
  orderIssueSheetProps: OrderIssueSheetProps
  loading?: boolean
  disabled?: boolean
}

// ViewModel dependencies interface
export interface HelpPageViewModelDependencies {
  helpPageService?: IHelpPageService
}

// ViewModel state interface
export interface HelpPageViewModelState {
  // Current section being displayed
  activeSection: HelpSection

  // Form state
  formMessage: string

  // Loading states
  loading: boolean
  submitting: boolean
  loadingOrders: boolean

  // Restaurant contact
  restaurantContact: RestaurantContactInfo | null

  // Order history for order issue flow
  orderHistory: Order[]
  selectedOrder: Order | null

  // Error handling
  error: string | null

  // Success state
  submitSuccess: boolean

  // Sheet states for overlay forms
  appIssueSheetOpen: boolean
  feedbackSheetOpen: boolean
  orderHistorySheetOpen: boolean
  orderIssueSheetOpen: boolean
}

// Order History Sheet props interface
interface OrderHistorySheetProps {
  orders?: Order[]
  onSelectOrder?: (order: Order) => void
  loading?: boolean
  disabled?: boolean
  testID?: string
  accessibilityLabel?: string
}

// Order Issue Sheet props interface
interface OrderIssueSheetProps {
  selectedOrder?: Order
  onBack?: () => void
  specialRequests?: string
  onSpecialRequestsChange?: (text: string) => void
  onSubmit: () => void
  loading?: boolean
  disabled?: boolean
  testID?: string
  accessibilityLabel?: string
}

// ViewModel actions interface
export interface HelpPageViewModelActions {
  // Navigation
  navigateToSection: (section: HelpSection) => void
  navigateBack: () => void

  // Form management
  handleFormMessageChange: (message: string) => void
  handleFormSubmit: () => Promise<void>
  resetForm: () => void

  // Phone call
  handleCallRestaurant: () => void

  // State management
  clearError: () => void
  setError: (error: string) => void

  // Sheet controls for overlay forms
  openAppIssueSheet: () => void
  closeAppIssueSheet: () => void
  openFeedbackSheet: () => void
  closeFeedbackSheet: () => void

  // Order issue sheet controls
  openOrderHistorySheet: () => void
  closeOrderHistorySheet: () => void
  openOrderIssueSheet: () => void
  closeOrderIssueSheet: () => void

  // Order selection
  handleSelectOrder: (order: Order) => void
  handleBackToOrderHistory: () => void

  // Component integration
  getHelpPageNavigationProps: () => HelpPageNavigationProps
  getOrderIssueFormProps: () => FormProps
  getAppIssueFormProps: () => FormProps
  getFeedbackFormProps: () => FormProps
  getPageContentHelpProps: () => PageContentHelpPropsResult
  getOrderHistorySheetProps: () => OrderHistorySheetProps
  getOrderIssueSheetProps: () => OrderIssueSheetProps
}

// Combined ViewModel interface
export interface HelpPageViewModel
  extends HelpPageViewModelState,
    HelpPageViewModelActions {}

/**
 * Platform-agnostic HelpPageViewModel factory
 * Uses dependency injection for platform-specific services
 * Returns a hook function that provides the ViewModel
 */
export const createHelpPageViewModel = (
  services: IPlatformServices,
  dependencies: HelpPageViewModelDependencies = {}
) => {
  return (): HelpPageViewModel => {
    // Dependencies
    const helpPageService = dependencies.helpPageService

    // Refs for cleanup and mount tracking
    const mountedRef = useRef(true)

    // State
    const [activeSection, setActiveSection] = useState<HelpSection>('navigation')
    const [formMessage, setFormMessage] = useState('')
    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [loadingOrders, setLoadingOrders] = useState(false)
    const [restaurantContact, setRestaurantContact] =
      useState<RestaurantContactInfo | null>(null)
    const [error, setErrorState] = useState<string | null>(null)
    const [submitSuccess, setSubmitSuccess] = useState(false)

    // Order history state
    const [orderHistory, setOrderHistory] = useState<Order[]>([])
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)

    // Sheet states for overlay forms
    const [appIssueSheetOpen, setAppIssueSheetOpen] = useState(false)
    const [feedbackSheetOpen, setFeedbackSheetOpen] = useState(false)
    const [orderHistorySheetOpen, setOrderHistorySheetOpen] = useState(false)
    const [orderIssueSheetOpen, setOrderIssueSheetOpen] = useState(false)

    // Load restaurant contact and order history on mount
    useEffect(() => {
      mountedRef.current = true

      const loadData = async () => {
        if (!helpPageService) return

        try {
          setLoading(true)
          setLoadingOrders(true)

          // Load restaurant contact and order history in parallel
          const [contact, orders] = await Promise.all([
            helpPageService.getRestaurantContactInfo(),
            helpPageService.getOrderHistory()
          ])

          if (mountedRef.current) {
            safeGetLogger()?.debug('Help page data loaded', { contact, ordersCount: orders?.length ?? 0 })
            setRestaurantContact(contact)
            setOrderHistory(orders || [])
          }
        } catch (err) {
          safeGetLogger()?.error('Help page data load failed', { error: String(err) })
        } finally {
          if (mountedRef.current) {
            setLoading(false)
            setLoadingOrders(false)
          }
        }
      }

      loadData()

      return () => {
        mountedRef.current = false
      }
    }, [helpPageService])

    // Navigation actions
    const navigateToSection = useCallback((section: HelpSection) => {
      // App Issue and Feedback open as sheets instead of page navigation
      if (section === 'app-issue') {
        setAppIssueSheetOpen(true)
        setActiveSection('app-issue') // Track for form submission type
        setFormMessage('')
        setSubmitSuccess(false)
        setErrorState(null)
      } else if (section === 'feedback') {
        setFeedbackSheetOpen(true)
        setActiveSection('feedback') // Track for form submission type
        setFormMessage('')
        setSubmitSuccess(false)
        setErrorState(null)
      } else if (section === 'order-issue') {
        // Order Issue opens order history sheet first
        setOrderHistorySheetOpen(true)
        setActiveSection('order-issue') // Track for form submission type
        setSelectedOrder(null)
        setFormMessage('')
        setSubmitSuccess(false)
        setErrorState(null)
      } else {
        // Navigation uses page navigation
        setActiveSection(section)
        setFormMessage('')
        setSubmitSuccess(false)
        setErrorState(null)
      }
    }, [])

    const navigateBack = useCallback(() => {
      setActiveSection('navigation')
      setFormMessage('')
      setSubmitSuccess(false)
      setErrorState(null)
    }, [])

    // Sheet control functions for overlay forms
    const openAppIssueSheet = useCallback(() => {
      setAppIssueSheetOpen(true)
      setActiveSection('app-issue')
      setFormMessage('')
      setSubmitSuccess(false)
      setErrorState(null)
    }, [])

    const closeAppIssueSheet = useCallback(() => {
      setAppIssueSheetOpen(false)
      setActiveSection('navigation')
      setFormMessage('')
      setSubmitSuccess(false)
      setErrorState(null)
    }, [])

    const openFeedbackSheet = useCallback(() => {
      setFeedbackSheetOpen(true)
      setActiveSection('feedback')
      setFormMessage('')
      setSubmitSuccess(false)
      setErrorState(null)
    }, [])

    const closeFeedbackSheet = useCallback(() => {
      setFeedbackSheetOpen(false)
      setActiveSection('navigation')
      setFormMessage('')
      setSubmitSuccess(false)
      setErrorState(null)
    }, [])

    // Order issue sheet control functions
    const openOrderHistorySheet = useCallback(() => {
      setOrderHistorySheetOpen(true)
      setActiveSection('order-issue')
      setSelectedOrder(null)
      setFormMessage('')
      setSubmitSuccess(false)
      setErrorState(null)
    }, [])

    const closeOrderHistorySheet = useCallback(() => {
      setOrderHistorySheetOpen(false)
      setActiveSection('navigation')
      setSelectedOrder(null)
      setFormMessage('')
      setSubmitSuccess(false)
      setErrorState(null)
    }, [])

    const openOrderIssueSheet = useCallback(() => {
      setOrderIssueSheetOpen(true)
    }, [])

    const closeOrderIssueSheet = useCallback(() => {
      setOrderIssueSheetOpen(false)
      setOrderHistorySheetOpen(false)
      setActiveSection('navigation')
      setSelectedOrder(null)
      setFormMessage('')
      setSubmitSuccess(false)
      setErrorState(null)
    }, [])

    // Order selection handlers
    const handleSelectOrder = useCallback((order: Order) => {
      setSelectedOrder(order)
      setOrderHistorySheetOpen(false)
      setOrderIssueSheetOpen(true)
      setFormMessage('')
      setSubmitSuccess(false)
      setErrorState(null)
    }, [])

    const handleBackToOrderHistory = useCallback(() => {
      setOrderIssueSheetOpen(false)
      setOrderHistorySheetOpen(true)
      setSelectedOrder(null)
      setFormMessage('')
      setSubmitSuccess(false)
      setErrorState(null)
    }, [])

    // Form actions
    const handleFormMessageChange = useCallback((message: string) => {
      setFormMessage(message)
    }, [])

    const handleFormSubmit = useCallback(async () => {
      if (!helpPageService || !formMessage.trim()) {
        services.platformService.showToast({
          message: 'Please enter a message',
          type: 'error'
        })
        return
      }

      try {
        setSubmitting(true)
        setErrorState(null)

        const formData: HelpFormData = {
          type: activeSection as 'order-issue' | 'app-issue' | 'feedback',
          message: formMessage.trim(),
          // Include orderId for order-issue submissions
          orderId: activeSection === 'order-issue' ? selectedOrder?.id : undefined
        }

        const result = await helpPageService.submitHelpRequest(formData)

        if (mountedRef.current) {
          if (result.success) {
            setSubmitSuccess(true)
            services.platformService.showToast({
              message: 'Your request has been submitted successfully',
              type: 'success'
            })
            // Close sheet or navigate back immediately
            if (activeSection === 'app-issue') {
              closeAppIssueSheet()
            } else if (activeSection === 'feedback') {
              closeFeedbackSheet()
            } else if (activeSection === 'order-issue') {
              closeOrderIssueSheet()
            } else {
              navigateBack()
            }
          } else {
            setErrorState(result.errorMessage || 'Submission failed')
            services.platformService.showToast({
              message: result.errorMessage || 'Failed to submit request',
              type: 'error'
            })
          }
        }
      } catch (err) {
        if (mountedRef.current) {
          const errorMessage =
            err instanceof Error ? err.message : 'Submission failed'
          setErrorState(errorMessage)
          services.platformService.showToast({
            message: 'Failed to submit request',
            type: 'error'
          })
        }
      } finally {
        if (mountedRef.current) {
          setSubmitting(false)
        }
      }
    }, [
      helpPageService,
      formMessage,
      activeSection,
      selectedOrder,
      services.platformService,
      navigateBack,
      closeAppIssueSheet,
      closeFeedbackSheet,
      closeOrderIssueSheet
    ])

    const resetForm = useCallback(() => {
      setFormMessage('')
      setSubmitSuccess(false)
      setErrorState(null)
    }, [])

    // Phone call action
    const handleCallRestaurant = useCallback(() => {
      if (!helpPageService || !restaurantContact?.phoneNumber) {
        services.platformService.showToast({
          message: 'Restaurant phone number not available',
          type: 'error'
        })
        return
      }
      helpPageService.callRestaurant(restaurantContact.phoneNumber)
    }, [helpPageService, restaurantContact, services.platformService])

    // State management helpers
    const clearError = useCallback(() => {
      setErrorState(null)
    }, [])

    const setError = useCallback((errorMessage: string) => {
      setErrorState(errorMessage)
    }, [])

    // Component props getters
    const getHelpPageNavigationProps = useCallback((): HelpPageNavigationProps => {
      const navItems: HelpNavItem[] = [
        {
          id: 'order-issue',
          pageTitle: 'Order Issue',
          iconName: 'orderIssue',
          onNavigate: () => navigateToSection('order-issue'),
          testID: 'help-nav-order-issue'
        },
        {
          id: 'app-issue',
          pageTitle: 'App Issue',
          iconName: 'questionApp',
          onNavigate: () => navigateToSection('app-issue'),
          testID: 'help-nav-app-issue'
        },
        {
          id: 'feedback',
          pageTitle: 'Feedback',
          iconName: 'lightbulb',
          onNavigate: () => navigateToSection('feedback'),
          testID: 'help-nav-feedback'
        },
        {
          id: 'call-restaurant',
          pageTitle: 'Call Restaurant',
          iconName: 'callOut',
          onNavigate: handleCallRestaurant,
          disabled: !restaurantContact?.phoneNumber,
          testID: 'help-nav-call-restaurant'
        }
      ]

      return {
        navItems,
        testID: 'help-page-navigation'
      }
    }, [navigateToSection, handleCallRestaurant, restaurantContact])

    const getOrderIssueFormProps = useCallback(
      (): FormProps => ({
        specialRequests: formMessage,
        onSpecialRequestsChange: handleFormMessageChange,
        onSubmit: handleFormSubmit,
        loading: submitting,
        disabled: submitting,
        testID: 'help-order-issue-form',
        accessibilityLabel: 'Order issue form'
      }),
      [formMessage, handleFormMessageChange, handleFormSubmit, submitting]
    )

    const getAppIssueFormProps = useCallback(
      (): FormProps => ({
        specialRequests: formMessage,
        onSpecialRequestsChange: handleFormMessageChange,
        onSubmit: handleFormSubmit,
        loading: submitting,
        disabled: submitting,
        testID: 'help-app-issue-form',
        accessibilityLabel: 'App issue form'
      }),
      [formMessage, handleFormMessageChange, handleFormSubmit, submitting]
    )

    const getFeedbackFormProps = useCallback(
      (): FormProps => ({
        specialRequests: formMessage,
        onSpecialRequestsChange: handleFormMessageChange,
        onSubmit: handleFormSubmit,
        loading: submitting,
        disabled: submitting,
        testID: 'help-feedback-form',
        accessibilityLabel: 'Feedback form'
      }),
      [formMessage, handleFormMessageChange, handleFormSubmit, submitting]
    )

    const getOrderHistorySheetProps = useCallback(
      (): OrderHistorySheetProps => ({
        orders: orderHistory,
        onSelectOrder: handleSelectOrder,
        loading: loadingOrders,
        disabled: false,
        testID: 'help-order-history-sheet',
        accessibilityLabel: 'Select an order to report an issue'
      }),
      [orderHistory, handleSelectOrder, loadingOrders]
    )

    const getOrderIssueSheetProps = useCallback(
      (): OrderIssueSheetProps => ({
        selectedOrder: selectedOrder || undefined,
        onBack: handleBackToOrderHistory,
        specialRequests: formMessage,
        onSpecialRequestsChange: handleFormMessageChange,
        onSubmit: handleFormSubmit,
        loading: submitting,
        disabled: submitting,
        testID: 'help-order-issue-sheet',
        accessibilityLabel: 'Report an issue with your order'
      }),
      [
        selectedOrder,
        handleBackToOrderHistory,
        formMessage,
        handleFormMessageChange,
        handleFormSubmit,
        submitting
      ]
    )

    const getPageContentHelpProps = useCallback(
      (): PageContentHelpPropsResult => ({
        tiles: [
          {
            id: 'order-issue',
            pageTitle: 'Order Issue',
            iconName: 'orderIssue',
            onClick: () => navigateToSection('order-issue'),
            // Disable if no orders in history
            disabled: orderHistory.length === 0,
            testID: 'help-tile-order-issue',
            accessibilityLabel: 'Report an order issue'
          },
          {
            id: 'app-issue',
            pageTitle: 'App Issue',
            iconName: 'questionApp',
            onClick: () => navigateToSection('app-issue'),
            testID: 'help-tile-app-issue',
            accessibilityLabel: 'Report an app issue'
          },
          {
            id: 'feedback',
            pageTitle: 'Feedback',
            iconName: 'lightbulb',
            onClick: () => navigateToSection('feedback'),
            testID: 'help-tile-feedback',
            accessibilityLabel: 'Provide feedback'
          },
          {
            id: 'call-restaurant',
            pageTitle: 'Call Restaurant',
            iconName: 'callOut',
            onClick: handleCallRestaurant,
            disabled: !restaurantContact?.phoneNumber,
            testID: 'help-tile-call-restaurant',
            accessibilityLabel: 'Call the restaurant'
          }
        ],
        appIssueSheetOpen,
        onAppIssueSheetClose: closeAppIssueSheet,
        feedbackSheetOpen,
        onFeedbackSheetClose: closeFeedbackSheet,
        appIssueFormProps: {
          specialRequests: formMessage,
          onSpecialRequestsChange: handleFormMessageChange,
          onSubmit: handleFormSubmit,
          loading: submitting,
          disabled: submitting,
          testID: 'help-app-issue-form',
          accessibilityLabel: 'App issue form'
        },
        feedbackFormProps: {
          specialRequests: formMessage,
          onSpecialRequestsChange: handleFormMessageChange,
          onSubmit: handleFormSubmit,
          loading: submitting,
          disabled: submitting,
          testID: 'help-feedback-form',
          accessibilityLabel: 'Feedback form'
        },
        // Order issue sheets
        orderHistorySheetOpen,
        onOrderHistorySheetClose: closeOrderHistorySheet,
        orderHistorySheetProps: {
          orders: orderHistory,
          onSelectOrder: handleSelectOrder,
          loading: loadingOrders,
          disabled: false,
          testID: 'help-order-history-sheet',
          accessibilityLabel: 'Select an order to report an issue'
        },
        orderIssueSheetOpen,
        onOrderIssueSheetClose: closeOrderIssueSheet,
        orderIssueSheetProps: {
          selectedOrder: selectedOrder || undefined,
          onBack: handleBackToOrderHistory,
          specialRequests: formMessage,
          onSpecialRequestsChange: handleFormMessageChange,
          onSubmit: handleFormSubmit,
          loading: submitting,
          disabled: submitting,
          testID: 'help-order-issue-sheet',
          accessibilityLabel: 'Report an issue with your order'
        },
        loading,
        disabled: false
      }),
      [
        navigateToSection,
        handleCallRestaurant,
        restaurantContact,
        orderHistory,
        appIssueSheetOpen,
        closeAppIssueSheet,
        feedbackSheetOpen,
        closeFeedbackSheet,
        orderHistorySheetOpen,
        closeOrderHistorySheet,
        handleSelectOrder,
        loadingOrders,
        orderIssueSheetOpen,
        closeOrderIssueSheet,
        selectedOrder,
        handleBackToOrderHistory,
        formMessage,
        handleFormMessageChange,
        handleFormSubmit,
        submitting,
        loading
      ]
    )

    return {
      // State
      activeSection,
      formMessage,
      loading,
      submitting,
      loadingOrders,
      restaurantContact,
      orderHistory,
      selectedOrder,
      error,
      submitSuccess,
      appIssueSheetOpen,
      feedbackSheetOpen,
      orderHistorySheetOpen,
      orderIssueSheetOpen,

      // Actions
      navigateToSection,
      navigateBack,
      handleFormMessageChange,
      handleFormSubmit,
      resetForm,
      handleCallRestaurant,
      clearError,
      setError,
      openAppIssueSheet,
      closeAppIssueSheet,
      openFeedbackSheet,
      closeFeedbackSheet,
      openOrderHistorySheet,
      closeOrderHistorySheet,
      openOrderIssueSheet,
      closeOrderIssueSheet,
      handleSelectOrder,
      handleBackToOrderHistory,
      getHelpPageNavigationProps,
      getOrderIssueFormProps,
      getAppIssueFormProps,
      getFeedbackFormProps,
      getPageContentHelpProps,
      getOrderHistorySheetProps,
      getOrderIssueSheetProps
    }
  }
}
